import { type ReactNode, type Ref, useRef, useState } from "react";
import { Popover } from "@mantine/core";
import {
  IconBrightness,
  IconContrast,
  IconRotateClockwise,
  IconSun,
} from "@tabler/icons-react";
import {
  BRIGHTNESS_RANGE,
  CONTRAST_RANGE,
  DEFAULT_IMAGE_ADJUSTMENTS,
  type ImageAdjustments,
  isAdjusted,
} from "./imageAdjustments";

type AdjustmentSliderProps = {
  inputRef?: Ref<HTMLInputElement>;
  label: string;
  icon: ReactNode;
  range: { min: number; max: number; step: number };
  value: number;
  onChange: (value: number) => void;
};

const AdjustmentSlider = ({
  inputRef,
  label,
  icon,
  range,
  value,
  onChange,
}: AdjustmentSliderProps) => (
  <label className="flex items-center gap-2 text-[11px] font-semibold text-white/85">
    {icon}
    <span className="w-16">{label}</span>
    <input
      ref={inputRef}
      type="range"
      min={range.min}
      max={range.max}
      step={range.step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="w-28 cursor-pointer accent-[#91A1FF]"
    />
    <span className="w-9 text-right tabular-nums">{value}%</span>
  </label>
);

type ImageAdjustmentsControlProps = {
  adjustments: ImageAdjustments;
  onChange: (adjustments: ImageAdjustments) => void;
};

export const ImageAdjustmentsControl = ({
  adjustments,
  onChange,
}: ImageAdjustmentsControlProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const brightnessRef = useRef<HTMLInputElement>(null);
  const isHighlighted = isOpen || isAdjusted(adjustments);

  return (
    <Popover
      opened={isOpen}
      onChange={setIsOpen}
      position="bottom-end"
      offset={8}
      radius={14}
      trapFocus
    >
      <Popover.Target>
        <button
          type="button"
          aria-label="Brightness and contrast"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          className={`flex h-9 w-9 items-center justify-center rounded-full text-white shadow transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
            isHighlighted
              ? "bg-[#5A6EFA] hover:bg-[#4d5ee6]"
              : "bg-[#2D364A]/60 hover:bg-[#2D364A]/80"
          }`}
        >
          <IconBrightness size={18} strokeWidth={2.25} />
        </button>
      </Popover.Target>

      {/* Keys pressed in the panel stay within it, so the page's shortcuts don't handle them. */}
      <Popover.Dropdown
        onKeyDown={(event) => {
          if (event.key !== "Tab") {
            event.stopPropagation();
          }
        }}
        // Dark and see-through, so the frame being adjusted stays visible behind the panel.
        className="flex flex-col gap-2 !border-0 !bg-[#111624]/70 !p-3 backdrop-blur-sm"
      >
        <AdjustmentSlider
          inputRef={brightnessRef}
          label="Brightness"
          icon={<IconSun size={14} strokeWidth={2.5} />}
          range={BRIGHTNESS_RANGE}
          value={adjustments.brightness}
          onChange={(brightness) => onChange({ ...adjustments, brightness })}
        />
        <AdjustmentSlider
          label="Contrast"
          icon={<IconContrast size={14} strokeWidth={2.5} />}
          range={CONTRAST_RANGE}
          value={adjustments.contrast}
          onChange={(contrast) => onChange({ ...adjustments, contrast })}
        />
        <div className="flex justify-end">
          <button
            type="button"
            // Moves focus to Brightness after resetting: Reset is then disabled, and a disabled
            // button loses focus to the page, where keys would reach its shortcuts.
            onClick={() => {
              onChange(DEFAULT_IMAGE_ADJUSTMENTS);
              brightnessRef.current?.focus();
            }}
            disabled={!isAdjusted(adjustments)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold text-white/80 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <IconRotateClockwise size={13} strokeWidth={2.5} />
            Reset
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};
